import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { PaperPlaneRight, Sparkle, CircleNotch, X } from "@phosphor-icons/react";

export default function CopilotChat({ assessmentId, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm your ORIXA Research Copilot. Ask me about risks, partners, regulatory steps, prioritised opportunities — anything in this dossier." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [chatId, setChatId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, streaming]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("orixa_token") || "";
      const resp = await fetch(`${API}/copilot/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ assessment_id: assessmentId, chat_id: chatId, message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.type === "meta") setChatId(data.chat_id);
          if (data.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + data.content };
              return copy;
            });
          }
          if (data.type === "done") { setStreaming(false); return; }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — the Copilot is unavailable. Please retry." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  const suggestions = [
    "What are the biggest risks?",
    "Which partners should I prioritise?",
    "Fastest market entry path?",
    "Which banks should I approach?",
  ];

  return (
    <div className="flex flex-col h-full bg-card" data-testid="copilot-chat">
      <div className="border-b border-border px-4 py-3 flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-teal-soft text-teal grid place-items-center">
          <Sparkle size={14} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base sm:text-lg leading-tight">Research Copilot</div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{streaming ? "Thinking…" : "Online"}</div>
        </div>
        {streaming && <CircleNotch size={14} className="animate-spin text-muted-foreground" />}
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label="Close" data-testid="copilot-close">
            <X size={18} weight="bold" />
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={`msg-${i}-${m.role}`} className={`text-sm ${m.role === "user" ? "flex justify-end" : ""}`} data-testid={`msg-${i}`}>
            <div className={`max-w-[88%] ${m.role === "user" ? "" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">{m.role === "user" ? "You" : "Copilot"}</div>
              <div className={`whitespace-pre-wrap leading-relaxed text-sm ${m.role === "user" ? "bg-primary text-primary-foreground px-3.5 py-2.5 rounded-2xl rounded-br-sm" : "text-foreground"}`}>
                {m.content || (streaming && i === messages.length - 1 ? <span className="text-muted-foreground italic">thinking…</span> : null)}
              </div>
            </div>
          </div>
        ))}

        {messages.length <= 1 && (
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Try asking</div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => { setInput(s); }} className="text-xs bg-secondary text-foreground border border-border rounded-full px-3 py-1.5 hover:bg-teal-soft hover:text-teal hover:border-primary/40 transition-colors" data-testid={`suggest-${s.slice(0,10)}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="border-t border-border p-3 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the Copilot…" disabled={streaming} className="flex-1 bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="copilot-input" />
        <button type="submit" disabled={streaming || !input.trim()} className="bg-primary text-primary-foreground rounded-lg px-3 py-2.5 disabled:opacity-50 hover:bg-primary/90 transition-colors" data-testid="copilot-send">
          <PaperPlaneRight size={16} weight="bold" />
        </button>
      </form>
    </div>
  );
}
